import { MedicineSimple } from '@dreryk/drerykweb-medicine-api';

import { isEmpty } from '~libs.typescript';

import { PreciseDosage } from '../types';

const MULTIPLE_PACKAGE_UNITS_QUANTITY = 2;

export type MedicineSufficiencyOptions = {
   medicine: MedicineSimple | undefined;
   preciseDosage: PreciseDosage | undefined;
};

type MedicineSufficiencyCalculationData = {
   singlePackageCapacity: number;
   dailyDose: number;
};

export const hasCompositePackage = (medicine: MedicineSimple | undefined): boolean => {
   return (medicine?.singlePackageQuantity ?? 0) >= MULTIPLE_PACKAGE_UNITS_QUANTITY;
};

export const hasKnownPackageCapacity = (medicine: MedicineSimple | undefined): boolean => {
   return !isEmpty(medicine?.singlePackageCapacity) && !isEmpty(medicine?.singlePackageUnit);
};

// Temporary algorithm until phx provides full set of calculation data
const getCalculationData = (options: MedicineSufficiencyOptions): MedicineSufficiencyCalculationData | undefined => {
   const { medicine, preciseDosage } = options;

   if (
      !preciseDosage?.frequency ||
      !preciseDosage.portion ||
      !preciseDosage.unit ||
      !medicine?.singlePackageCapacity ||
      !medicine.singlePackageUnit ||
      medicine.singlePackageUnit !== preciseDosage.unit ||
      hasCompositePackage(medicine)
   ) {
      return undefined;
   }

   return {
      singlePackageCapacity: medicine.singlePackageCapacity,
      dailyDose: preciseDosage.frequency * preciseDosage.portion,
   };
};

export const getMedicineSufficiencyCount = (
   options: MedicineSufficiencyOptions & { packagesCount: number | null | undefined }
): number | undefined => {
   const calculationData = getCalculationData(options);

   if (!calculationData || !options.packagesCount) {
      return undefined;
   }

   return Math.floor((options.packagesCount * calculationData.singlePackageCapacity) / calculationData.dailyDose);
};

export const isDosageExceedingPackages = (
   options: MedicineSufficiencyOptions & { packagesCount: number | null | undefined }
): boolean => {
   const calculationData = getCalculationData(options);

   if (!calculationData || !options.packagesCount) {
      return false;
   }

   return options.packagesCount * calculationData.singlePackageCapacity < calculationData.dailyDose;
};

export const getPackagesCountForTreatmentDaysCount = (
   options: MedicineSufficiencyOptions & { treatmentDaysCount: number | null | undefined }
): number | undefined => {
   const calculationData = getCalculationData(options);

   if (!calculationData || !options.treatmentDaysCount) {
      return undefined;
   }

   return Math.ceil((options.treatmentDaysCount * calculationData.dailyDose) / calculationData.singlePackageCapacity);
};
